<template>
  <Dialog
    v-model="isVisible"
    title="Edit Contact Information"
    dialog-type="primary"
    dialog-size="medium"
    title-variant="medium"
    :header-icon-src="require('@/assets/MyPatient/sendTask_white.svg')"
    :close-on-overlay="false"
    @update:model-value="isVisible = $event"
    @close="handleClose"
  >
    <!-- Body -->
    <div v-loading="loading" class="dialog-content">
      <!-- Email and Phone Row -->
      <div class="flex justify-between gap-4">
        <div class="w-1/2">
          <InfoContainer
            :message="emailErrorMessage"
            :state="emailError ? 'error' : 'alert'"
          >
            <Label label="Email" :required="true">
              <TextInput
                v-model="formData.email"
                type="email"
                size="medium"
                placeholder="example@email.com"
              />
            </Label>
          </InfoContainer>
        </div>

        <div class="w-1/2">
          <InfoContainer
            :message="phoneErrorMessage"
            state="error"
          >
            <Label label="Phone Number" :required="true">
              <AreaCodePhoneInput
                v-model="phoneInputModel"
                size="medium"
                :country_filterable="true"
              />
            </Label>
          </InfoContainer>
        </div>
      </div>

      <!-- Address Section Title -->
      <div class="section-title ml-3 mt-3 mb-6">Address Information</div>

      <!-- Address Fields using AddressFormFields component -->
      <AddressFormFields
        v-model="addressFormData"
        :showErrors="showErrors"
        :showAddressEditIndicator="showInvalidWarning && !addressAcknowledged"
        @address-edit="handleAddressEdit"
      />

      <!-- Invalid Address Warning -->
      <InvalidAddressWarning
        v-if="showInvalidWarning"
        :show-use-anyway-button="true"
        @acknowledged="handleAddressAcknowledged"
        @close="showInvalidWarning = false"
      />

      <!-- Address Selection (Original vs Recommended) -->
      <AddressSelectionCard
        v-model="selectedAddress"
        :visible="showAddressSelection"
        :original-address="originalAddressPreview"
        :suggested-address="suggestedAddressPreview"
      />
    </div>

    <!-- Footer -->
    <template #footer>
      <div class="footer-actions">
        <ResponsiveButton
          button_type="outlined"
          built_in_theme="error"
          size="medium"
          display_name="Cancel"
          @click-button="handleClose"
        />
        <ResponsiveButton
          button_type="filled"
          built_in_theme="dark"
          size="medium"
          :display_name="saveLoading ? 'Saving...' : 'Save Changes'"
          :disabled="!isFormValid || (showInvalidWarning && !addressAcknowledged) || (showAddressSelection && !selectedAddress)"
          @click-button="handleSave"
        />
      </div>
    </template>
  </Dialog>
</template>

<script>
import AddressFormFields from '@/components/AddressForm/AddressFormFields.vue';
import InvalidAddressWarning from '@/components/AddressForm/InvalidAddressWarning.vue';
import AddressSelectionCard from '@/components/AddressForm/AddressSelectionCard.vue';
import { updatePatientInfoApi } from '@/api/patient';
import { validateAddressWithFedex } from '@/utils/addressValidation';
import { hasShippingAddressLengthError } from '@/utils/shippingAddressLimits';
import { notify } from '@/utils/notify';

export default {
  name: 'EditContactAddressDialog',
  components: {
    AddressFormFields,
    InvalidAddressWarning,
    AddressSelectionCard,
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    patientId: {
      type: [String, Number],
      required: true
    },
    roomId: {
      type: String,
      required: true
    }
  },
  emits: ['update:visible', 'saved'],
  data() {
    return {
      isVisible: this.visible,
      loading: false,
      saveLoading: false,
      showErrors: false,
      patientData: null,
      formData: {
        email: '',
      },
      phoneInputModel: { area_code: '1', phone_number: '' },
      addressFormData: {
        country: '',
        streetAddress: '',
        city: '',
        state: '',
        zipcode: ''
      },
      showInvalidWarning: false,
      addressChanged: false,
      addressAcknowledged: false,
      showAddressSelection: false,
      selectedAddress: '',
      originalAddressPreview: '',
      suggestedAddressPreview: '',
      suggestedResolvedAddress: null,
      userModifiedStreetAddress: '',
      emailError: ''
    };
  },
  computed: {
    isStateRequired() {
      const stateRequiredCountries = ['US', 'CA', 'AU'];
      return stateRequiredCountries.includes(this.addressFormData.country);
    },
    isFormValid() {
      const baseFieldsComplete = this.formData.email &&
                                  this.phoneInputModel.phone_number &&
                                  this.addressFormData.country &&
                                  this.addressFormData.streetAddress &&
                                  this.addressFormData.city &&
                                  this.addressFormData.zipcode;

      // Only require state if it's required for the selected country
      const stateComplete = !this.isStateRequired || this.addressFormData.state;

      // Email must be valid if provided
      const emailValid = !this.emailError;

      // VP-18765: a field over the shipping length limit blocks the save; the
      // address form shows which.
      const lengthsOk = !hasShippingAddressLengthError(this.addressFormData);

      return baseFieldsComplete && stateComplete && emailValid && lengthsOk;
    },
    emailErrorMessage() {
      if (this.emailError) {
        return this.emailError;
      }
      if (this.showErrors && !this.formData.email) {
        return 'Required fields cannot be empty.';
      }
      return '';
    },
    phoneErrorMessage() {
      if (this.showErrors && !this.phoneInputModel.phone_number) {
        return 'Required fields cannot be empty.';
      }
      return '';
    }
  },
  watch: {
    visible(newVal) {
      this.isVisible = newVal;
      if (newVal) {
        this.fetchPatientData();
      } else {
        this.resetForm();
      }
    },
    isVisible(newVal) {
      this.$emit('update:visible', newVal);
    },
    'formData.email'(newValue) {
      this.validateEmail(newValue);
    }
  },
  methods: {
    validateEmail(email) {
      if (!email) {
        this.emailError = '';
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.emailError = 'Please enter a valid email address';
      } else {
        this.emailError = '';
      }
    },
    fetchPatientData() {
      this.loading = true;
      try {
        // Load data from rooms store instead of API call
        const room = this.$store.getters['myPatients/rooms/getPatientByRoomId'](this.roomId);

        if (!room || !room.patientBasicInfo) {
          notify({
            title: 'Error',
            message: 'Patient data not found in store',
            type: 'error'
          });
          this.loading = false;
          return;
        }

        const patient = room.patientBasicInfo;

        // Store patient ID for API update later
        this.patientData = {
          patient_id: patient.patientId
        };

        // Get phone and email from store
        const phone = patient.phone || '';
        const email = patient.email || '';

        this.formData = {
          email: email,
        };

        // Parse phone number in format "+1-1111111111" or "+1 1111111111"
        // Extract area code and phone number
        let areaCode = '1'; // default
        let phoneNumber = '';

        if (phone && phone.includes('+')) {
          const phoneMatch = phone.match(/^\+(\d+)[- ]?(\d+)$/);
          if (phoneMatch) {
            areaCode = phoneMatch[1];
            phoneNumber = phoneMatch[2];
          }
        } else {
          // No area code found, use the whole phone as phone number
          phoneNumber = phone.replace(/^\+\d+[- ]?/, '');
        }

        this.phoneInputModel = {
          area_code: areaCode,
          phone_number: phoneNumber
        };

        // Use addressObj from store which has structured address data
        if (patient.addressObj) {
          this.addressFormData = {
            country: patient.addressObj.country || 'US',
            streetAddress: patient.addressObj.streetAddress || '',
            city: patient.addressObj.city || '',
            state: patient.addressObj.state || '',
            zipcode: patient.addressObj.zipCode || ''  // Note: zipCode in store, zipcode in form
          };
        } else {
          // Fallback to default if no addressObj
          this.addressFormData = {
            country: 'US',
            streetAddress: '',
            city: '',
            state: '',
            zipcode: ''
          };
        }

        this.addressChanged = false;
        this.addressAcknowledged = false;
        this.showInvalidWarning = false;
        this.showAddressSelection = false;
      } catch (error) {
        console.error('Error loading patient data from store:', error);
        notify({
          title: 'Error',
          message: 'Failed to load patient data',
          type: 'error'
        });
      } finally {
        this.loading = false;
      }
    },

    handleAddressEdit() {
      this.addressChanged = true;
      this.addressAcknowledged = false;
      this.showInvalidWarning = false;
      this.showAddressSelection = false;
    },

    handleAddressAcknowledged() {
      this.addressAcknowledged = true;
    },

    async handleSave() {
      // Safety check: ensure patientData exists
      if (!this.patientData) {
        notify({
          title: 'Error',
          message: 'Patient data not found. Please close and reopen the dialog.',
          type: 'error'
        });
        return;
      }

      if (this.showAddressSelection && this.selectedAddress) {
        this.handleAddressSelectionConfirm();
        return;
      }

      if (!this.isFormValid) {
        this.showErrors = true;
        return;
      }

      this.showErrors = true;
      this.userModifiedStreetAddress = this.addressFormData.streetAddress;

      if (!this.addressChanged) {
        this.finalizeAddressSave();
        return;
      }

      this.showInvalidWarning = false;
      this.showAddressSelection = false;
      this.saveLoading = true;

      try {
        const fedexResult = await validateAddressWithFedex(
          this.addressFormData.streetAddress,
          this.addressFormData.city,
          this.addressFormData.state,
          this.addressFormData.zipcode,
          this.addressFormData.country
        );

        if (fedexResult.status === 'invalid') {
          this.showInvalidWarning = true;
          this.addressChanged = false;
          this.addressAcknowledged = false;
          this.saveLoading = false;
          return;
        }

        if (fedexResult.status === 'suggested') {
          const parts = [this.addressFormData.streetAddress, this.addressFormData.city, this.addressFormData.state, this.addressFormData.zipcode, this.addressFormData.country].filter(Boolean);
          const inputStr = parts.join(', ').toUpperCase().replace(/\s+/g, ' ').trim();
          const suggestedStreet = (fedexResult.resolved.street_lines_token || []).join(' ');
          const suggestedParts = [
            suggestedStreet,
            fedexResult.resolved.city,
            fedexResult.resolved.state_or_province_code,
            fedexResult.resolved.postal_code,
            fedexResult.resolved.country_code
          ].filter(Boolean);
          const suggestedStr = suggestedParts.join(', ').toUpperCase().replace(/\s+/g, ' ').trim();

          this.originalAddressPreview = inputStr;
          this.suggestedAddressPreview = suggestedStr;
          this.suggestedResolvedAddress = fedexResult.resolved;
          this.selectedAddress = '';
          this.showAddressSelection = true;
          this.saveLoading = false;
          return;
        }

        this.finalizeAddressSave();
      } catch (error) {
        console.error('Address validation error:', error);
        notify({
          title: 'Warning',
          message: 'Address validation service unavailable. Proceeding with save.',
          type: 'warning'
        });
        this.saveLoading = false;
        this.finalizeAddressSave();
      }
    },

    handleAddressSelectionConfirm() {
      if (this.selectedAddress === 'suggested' && this.suggestedResolvedAddress) {
        const resolved = this.suggestedResolvedAddress;
        const suggestedStreet = (resolved.street_lines_token || []).join(' ');
        this.addressFormData.streetAddress = suggestedStreet;
        this.addressFormData.city = resolved.city;
        this.addressFormData.state = resolved.state_or_province_code;
        this.addressFormData.zipcode = resolved.postal_code;
        this.addressFormData.country = resolved.country_code;
        this.showAddressSelection = false;
        this.finalizeAddressSave();
      } else if (this.selectedAddress === 'original') {
        this.addressFormData.streetAddress = this.userModifiedStreetAddress;
        this.showAddressSelection = false;
        this.finalizeAddressSave();
      }
    },

    async finalizeAddressSave() {
      // Safety check: ensure patientData exists
      if (!this.patientData) {
        notify({
          title: 'Error',
          message: 'Patient data not found. Please close and reopen the dialog.',
          type: 'error'
        });
        this.saveLoading = false;
        return;
      }

      this.saveLoading = true;

      try {
        // Format phone with area code prefix (e.g., "+1-1111111111")
        const formattedPhone = `+${this.phoneInputModel.area_code}-${this.phoneInputModel.phone_number}`;

        // Only send required fields to new API endpoint
        const updateData = {
          patient_id: this.patientData.patient_id,
          street_address: this.addressFormData.streetAddress,
          city: this.addressFormData.city,
          state: this.addressFormData.state,
          zipcode: this.addressFormData.zipcode,
          country: this.addressFormData.country,
          phone: formattedPhone,
          email: this.formData.email
        };

        const response = await updatePatientInfoApi(updateData);

        // Update the rooms store (this is where ContactCard gets its data from)
        this.$store.dispatch('myPatients/rooms/updatePatientContactInfo', {
          roomId: this.roomId,
          contactInfo: {
            phone: formattedPhone,
            email: this.formData.email,
            street_address: this.addressFormData.streetAddress,
            city: this.addressFormData.city,
            state: this.addressFormData.state,
            zipcode: this.addressFormData.zipcode,
            country: this.addressFormData.country
          }
        });

        notify({
          title: 'Success',
          message: 'Patient information updated successfully',
          type: 'success'
        });
        this.$emit('saved');
        this.handleClose();
      } catch (error) {
        console.error('Error updating patient:', error);
        notify({
          title: 'Error',
          message: 'Failed to update patient information',
          type: 'error'
        });
      } finally {
        this.saveLoading = false;
      }
    },

    handleClose() {
      this.isVisible = false;
      this.resetForm();
    },

    resetForm() {
      this.showErrors = false;
      this.addressChanged = false;
      this.addressAcknowledged = false;
      this.showInvalidWarning = false;
      this.showAddressSelection = false;
      this.selectedAddress = '';
      this.patientData = null;
      this.phoneInputModel = { area_code: '1', phone_number: '' };
    }
  }
};
</script>

<style scoped>
.dialog-content {
  padding: 20px 0px;
}

.footer-actions {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
}

.footer-actions :deep(.responsive-button) {
  min-width: 120px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}
</style>
